import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { CanticleCreate, CanticleStatus, Canticle } from '../types';
import { apiService } from '../services/api';

interface CanticleFormProps {
  onSubmit: (data: CanticleCreate, pdfFile?: File) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
  initialData?: Canticle;
}

export const CanticleForm: React.FC<CanticleFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  error,
  initialData,
}) => {
  const [formData, setFormData] = useState<CanticleCreate>({
    title: initialData?.title || '',
    lyrics: initialData?.lyrics || '',
    pdf_path: initialData?.pdf_path || '',
    status: initialData?.status || CanticleStatus.DRAFT,
  });
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const isEditing = !!initialData?.id;

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        lyrics: initialData.lyrics || '',
        pdf_path: initialData.pdf_path || '',
        status: initialData.status || CanticleStatus.DRAFT,
      });
      setPdfFile(null);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.lyrics) return;
    await onSubmit(formData, pdfFile || undefined);
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPdfFile(e.target.files[0]);
    }
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-900'>
          {isEditing ? 'Modifier le cantique' : 'Créer un nouveau cantique'}
        </h2>
      </div>

      {error && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Titre *
          </label>
          <input
            type='text'
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder='Titre du cantique'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Paroles *
          </label>
          <textarea
            value={formData.lyrics}
            onChange={(e) =>
              setFormData({ ...formData, lyrics: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            rows={15}
            placeholder='Paroles du cantique'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            PDF (optionnel)
          </label>
          {initialData?.pdf_path && (
            <div className='mb-3'>
              <p className='text-sm text-gray-600 mb-2'>PDF actuel :</p>
              <a
                href={apiService.getCanticlePdfUrl(initialData.id)}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors'
              >
                <FileText className='w-4 h-4' />
                <span>Voir le PDF actuel</span>
              </a>
            </div>
          )}
          <input
            type='file'
            accept='application/pdf'
            onChange={handlePdfChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          {pdfFile && (
            <p className='mt-1 text-sm text-gray-500'>
              Nouveau fichier sélectionné: {pdfFile.name}
            </p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Statut
          </label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({
                ...formData,
                status: e.target.value as CanticleStatus,
              })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            <option value={CanticleStatus.DRAFT}>Brouillon</option>
            <option value={CanticleStatus.PUBLISHED}>Publié</option>
            <option value={CanticleStatus.ARCHIVED}>Archivé</option>
          </select>
        </div>

        <div className='flex gap-3 pt-4'>
          <button
            type='submit'
            disabled={loading || !formData.title || !formData.lyrics}
            className='flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {loading
              ? isEditing
                ? 'Mise à jour...'
                : 'Création...'
              : isEditing
              ? 'Mettre à jour'
              : 'Créer le cantique'}
          </button>
          <button
            type='button'
            onClick={onCancel}
            className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50'
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
